import { Link } from '@inertiajs/react'
import type { ReactNode } from 'react'
import Layout from '@/components/Layout'
import {
  HeritageBadge,
  TrustStrip,
  PrimaryButton,
  GhostButton,
  PolaroidNote,
  Eyebrow,
  TicketStub,
} from '@/components/atoms'
import { BreadcrumbJsonLd } from '@/components/shared/json-ld'
import {
  CO_TAX_CREDIT,
  CO_TAX_CREDIT_URL,
  CO_REBATE_BASE,
  CO_REBATE_CARGO_BONUS,
} from '@/lib/constants'

// SOURCES (verified 2026-08-06 against the live state pages):
//   https://energyoffice.colorado.gov/ebike-tax-credit
//     "$225 discount per e-bike (limited to one discount a year per resident)"
//     — the discount is ONE PER CALENDAR YEAR. The previous copy claimed a
//     two-year cadence and a "$900 family bump"; both were wrong, and both
//     are corrected below.
//     Eligibility — every Colorado resident; proof of Colorado address is
//     required at purchase; there is no income test.
//     Bike must be new, UL 2849 or EN 15194 certified, motor 750W or less,
//     Class 1/2/3. No minimum or maximum purchase price.
//     "The Colorado E-Bike Tax Credit Discount can be combined with a
//      Colorado E-Bike Rebate at retailers participating in both programs."
//   Participating-retailer list (Front Range Cargo Bikes confirmed present):
//     https://docs.google.com/spreadsheets/d/1VTb1sOUb5He3iUdHTwBWngxP-MbhpuilPgDQ8RMBKGI
const eligibilityCards = [
  {
    numeral: '01',
    title: 'Colorado resident',
    body: "Anywhere in the state. Boulder, Denver, the Western Slope — all good. Bring a Colorado ID or a recent utility bill dated within three months.",
  },
  {
    numeral: '02',
    title: 'Buying a qualifying ebike',
    body: "New Class 1, 2, or 3 electric bicycle from a participating retailer — UL 2849 or EN 15194 certified, motor 750W or under. We’re on the state list, and most of our cargo bikes qualify. We’ll flag any that don’t.",
  },
  {
    numeral: '03',
    title: 'Once per calendar year',
    body: 'One discount per Colorado resident per calendar year. Two adults in a household can each take one, so a household buying two bikes in the same year gets both.',
  },
]

const howItWorksSteps = [
  {
    hand: 'step 1',
    t: 'Pick the bike',
    d: 'Test ride, fall in love, decide.',
  },
  {
    hand: 'step 2',
    t: 'We file the form',
    d: 'Right at the counter. Bring your ID, takes 4 minutes.',
  },
  {
    hand: 'step 3',
    t: `$${CO_TAX_CREDIT} off, on the spot`,
    d: 'Discount applied to your total. You ride home with it.',
  },
]

const faqItems = [
  {
    q: 'Can I stack it with trade-in?',
    a: 'Yes. Trade-in is separate, applied first.',
  },
  {
    q: 'Is it taxable?',
    a: "No — it comes off the price at checkout. Note that Colorado calculates sales tax on the listed retail price, not the discounted price, so the tax line won't shrink.",
  },
  {
    q: "What if I’m buying for a partner?",
    a: 'Each Colorado resident gets one discount per calendar year. Two adults in a household can each take one.',
  },
  {
    q: 'Online order?',
    a: 'The discount requires an in-person proof-of-address check. Walk-in or scheduled pickup both work.',
  },
  {
    q: 'Is this the same as the $1,000 rebate I keep hearing about?',
    a: `No — that's a separate income-qualified lottery program worth $${CO_REBATE_BASE.toLocaleString()} plus $${CO_REBATE_CARGO_BONUS} more on a cargo bike, and it stacks on top of this $${CO_TAX_CREDIT}. We're on the state list for both.`,
  },
]

function TaxVoucher() {
  return (
    <>
      <BreadcrumbJsonLd items={[{ name: 'CO E-Bike Tax Credit', href: '/tax-voucher' }]} />

      {/* ── Hero ── */}
      <section className="bg-[var(--background-warm)] py-16 pb-22 overflow-hidden relative">
        {/* Topo-line SVG background */}
        <svg
          aria-hidden="true"
          className="absolute inset-0 w-full h-full pointer-events-none"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1200 600"
          preserveAspectRatio="xMidYMid slice"
        >
          <path d="M0 120 Q200 80 400 140 T800 100 T1200 130" fill="none" stroke="var(--primary)" strokeWidth="1.2" opacity="0.14" />
          <path d="M0 200 Q300 160 600 220 T1200 180" fill="none" stroke="var(--primary)" strokeWidth="0.6" opacity="0.14" />
          <path d="M0 280 Q250 240 500 300 T1000 260 T1200 290" fill="none" stroke="var(--primary)" strokeWidth="1.2" opacity="0.14" />
          <path d="M0 360 Q350 320 700 380 T1200 340" fill="none" stroke="var(--primary)" strokeWidth="0.6" opacity="0.14" />
          <path d="M0 440 Q200 400 500 460 T900 420 T1200 450" fill="none" stroke="var(--primary)" strokeWidth="1.2" opacity="0.14" />
          <path d="M-100 60 Q200 20 500 80 T1100 40 T1300 70" fill="none" stroke="var(--primary)" strokeWidth="0.6" opacity="0.14" />
          <path d="M-100 520 Q300 480 600 540 T1100 500 T1300 530" fill="none" stroke="var(--primary)" strokeWidth="1.2" opacity="0.14" />
          <path d="M0 160 Q150 120 350 170 T750 130 T1200 160" fill="none" stroke="var(--primary)" strokeWidth="0.6" opacity="0.14" />
          <path d="M0 320 Q400 280 700 340 T1200 300" fill="none" stroke="var(--primary)" strokeWidth="1.2" opacity="0.14" />
        </svg>

        <div className="container relative">
          <div className="grid grid-cols-1 md:grid-cols-[1.2fr_1fr] gap-[56px] items-center">
            {/* Left column */}
            <div>
              <HeritageBadge>Colorado eBike Tax Voucher · 2026</HeritageBadge>
              {/* SOURCE: lib/constants.ts (CO_TAX_CREDIT + CO_TAX_CREDIT_URL).
                  The authoritative source URL was resolved and verified live on
                  2026-08-06: https://energyoffice.colorado.gov/ebike-tax-credit
                  states "$225 discount per e-bike (limited to one discount a
                  year per resident)". It is linked in the fine print below. */}
              <h1 className="mt-5 text-4xl md:text-5xl font-bold tracking-tight text-foreground leading-tight">
                Save{' '}
                <span className="text-accent">${CO_TAX_CREDIT}</span>{' '}
                on your next e-bike.{' '}
                <span className="hand text-[88px] text-primary">seriously.</span>
              </h1>
              <p className="mt-5 text-lg text-foreground leading-relaxed max-w-xl">
                Colorado residents get ${CO_TAX_CREDIT} back on most ebikes — including almost every cargo bike on our floor. We handle the paperwork at checkout. You ride home with the rebate already in your pocket.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Link href="/bikes">
                  <PrimaryButton size="lg" hand="See eligible bikes" sub={`$${CO_TAX_CREDIT} off · stackable with trade-in`} />
                </Link>
                <Link href="/book">
                  <GhostButton size="lg" sub="Free 30-min on the Creek Path">Test ride one first →</GhostButton>
                </Link>
              </div>

              <p className="mt-6 text-sm text-muted-foreground max-w-xl">
                Income-qualified? Colorado&apos;s separate{' '}
                <Link href="/rebate" className="underline text-primary font-semibold">
                  ${CO_REBATE_BASE.toLocaleString()} e-bike rebate
                </Link>{' '}
                stacks on top of this one — another ${CO_REBATE_CARGO_BONUS} on a cargo
                bike. It&apos;s a lottery with a registration deadline, so check the dates.
              </p>
            </div>

            {/* Right column — TicketStub */}
            <div className="flex justify-center">
              <div style={{ transform: 'rotate(2deg)' }}>
                <TicketStub />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── TrustStrip ── */}
      <TrustStrip items={['Colorado resident · check', 'ebike on this list · check', 'we file the paperwork · check']} />

      {/* ── Eligibility ── */}
      <section className="bg-[var(--background)] py-20">
        <div className="container">
          <Eyebrow className="text-accent">Eligibility · 3 boxes to tick</Eyebrow>
          <h2 className="mt-3 text-3xl md:text-4xl font-bold text-foreground">
            If you ride here,{' '}
            <span className="hand text-[52px] text-primary">if you live here.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-[22px] mt-9">
            {eligibilityCards.map((card) => (
              <div
                key={card.numeral}
                className="bg-card border border-border rounded-[var(--radius)] p-7 shadow-[var(--shadow-sm)]"
              >
                <div className="hand text-[52px] text-accent leading-none mb-3.5">{card.numeral}</div>
                <h3 className="text-xl font-semibold text-foreground mb-2">{card.title}</h3>
                <p className="text-sm text-foreground leading-relaxed">{card.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="bg-primary py-20 relative overflow-hidden">
        {/* Topo-line SVG overlay */}
        <svg
          aria-hidden="true"
          className="absolute inset-0 w-full h-full pointer-events-none"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1200 600"
          preserveAspectRatio="xMidYMid slice"
        >
          <path d="M0 100 Q300 60 600 120 T1200 80" fill="none" stroke="var(--secondary)" strokeWidth="1.2" opacity="0.12" />
          <path d="M0 200 Q250 160 500 220 T1000 180 T1200 210" fill="none" stroke="var(--secondary)" strokeWidth="0.8" opacity="0.12" />
          <path d="M0 300 Q350 260 700 320 T1200 280" fill="none" stroke="var(--secondary)" strokeWidth="1.2" opacity="0.12" />
          <path d="M0 400 Q200 360 500 420 T900 380 T1200 410" fill="none" stroke="var(--secondary)" strokeWidth="0.8" opacity="0.12" />
          <path d="M-100 500 Q300 460 600 520 T1100 480 T1300 510" fill="none" stroke="var(--secondary)" strokeWidth="1.2" opacity="0.12" />
          <path d="M0 150 Q400 110 800 170 T1200 130" fill="none" stroke="var(--secondary)" strokeWidth="0.8" opacity="0.12" />
          <path d="M0 350 Q300 310 600 370 T1200 330" fill="none" stroke="var(--secondary)" strokeWidth="1.2" opacity="0.12" />
          <path d="M-100 50 Q200 10 500 70 T1000 30 T1300 60" fill="none" stroke="var(--secondary)" strokeWidth="0.8" opacity="0.12" />
        </svg>

        <div className="container relative">
          <Eyebrow className="text-secondary mb-3.5">How it works · 3 steps</Eyebrow>
          <h2 className="text-3xl md:text-4xl font-bold text-background">
            We make the rebate{' '}
            <span className="hand text-[56px] text-secondary">boringly easy.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-[22px] mt-9">
            {howItWorksSteps.map((step) => (
              <div
                key={step.hand}
                className="bg-[rgba(255,248,231,0.06)] border border-[rgba(255,215,0,0.3)] rounded-[var(--radius)] p-7 relative"
              >
                <div className="hand text-[22px] text-secondary mb-3.5">{step.hand}</div>
                <h3 className="text-background text-2xl mb-2.5">{step.t}</h3>
                <p className="text-[rgba(255,248,231,0.82)] text-[15px] leading-[1.55]">{step.d}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 flex justify-center">
            <Link href="/book">
              <PrimaryButton size="lg" invert hand="Book a test ride" sub="We'll have the eligible bikes ready" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── FAQ / Fine Print ── */}
      <section className="bg-[var(--background-warm)] py-[72px]">
        <div className="container grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          {/* Left — PolaroidNote */}
          <div className="flex justify-center md:justify-start">
            {/* 2026-05-18: stripped fabricated "~600 families" + "$1,200+"
                stack figure (leaned on the invented trade-in range). The
                CO e-bike tax credit itself is real public CO law. */}
            <PolaroidNote signoff="— Ryan" rotate={-1.5}>
              The Colorado e-bike tax credit takes real money off a new
              family hauler — and you can stack it with a trade-in. Come
              in and we&apos;ll walk you through it.
            </PolaroidNote>
          </div>

          {/* Right — FAQ */}
          <div>
            <Eyebrow className="text-accent mb-3">The fine print, in english</Eyebrow>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-6">Questions we get all the time.</h2>
            <div>
              {faqItems.map((item) => (
                <div
                  key={item.q}
                  className="py-3 border-b border-dashed border-[var(--border-strong)]"
                >
                  <p className="font-semibold text-primary text-[15px] mb-1">{item.q}</p>
                  <p className="text-sm text-foreground">{item.a}</p>
                </div>
              ))}
            </div>

            <p className="mt-5 text-sm text-muted-foreground">
              Official terms, eligible-bike rules, and the current amount:{' '}
              <a
                href={CO_TAX_CREDIT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="underline text-primary"
              >
                Colorado Energy Office · E-Bike Tax Credit
              </a>
              .
            </p>
          </div>
        </div>
      </section>
    </>
  )
}

TaxVoucher.layout = (page: ReactNode) => <Layout>{page}</Layout>

export default TaxVoucher
